import { Link, router } from '@inertiajs/react'
import { useEffect, useRef } from 'react'
import InfoTip from '../../../components/InfoTip'

// ─── Constants ────────────────────────────────────────────────────────────────

const CARRIER_COLORS = {
  B6: '#003087', DL: '#E01933', UA: '#005DAA', AA: '#0078D2', WN: '#304CB2',
}

const CARRIER_NAMES = {
  B6: 'JetBlue Airways', DL: 'Delta Air Lines', UA: 'United Airlines',
  AA: 'American Airlines', WN: 'Southwest Airlines',
}

// ─── Mini risk bar ────────────────────────────────────────────────────────────

function MiniBar({ value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 bg-gray-100 rounded-full h-2 overflow-hidden flex-shrink-0">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs font-bold tabular-nums" style={{ color }}>{value}</span>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function CarrierMetrics({ metrics, tenant_branding }) {
  const intervalRef = useRef(null)

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      router.reload({ only: ['metrics'] })
    }, 60_000)
    return () => clearInterval(intervalRef.current)
  }, [])

  const sortedMetrics = [...metrics].sort((a, b) => b.pnr_count - a.pnr_count)
  const maxPnr        = sortedMetrics[0]?.pnr_count || 1
  const maxRisk       = Math.max(...sortedMetrics.map(m => m.high_risk_count), 1)
  const maxDispatch   = Math.max(...sortedMetrics.map(m => m.dispatch_count || 0), 1)

  const totals = {
    pnr_count:       sortedMetrics.reduce((n, m) => n + m.pnr_count, 0),
    high_risk_count: sortedMetrics.reduce((n, m) => n + m.high_risk_count, 0),
    dispatch_count:  sortedMetrics.reduce((n, m) => n + (m.dispatch_count || 0), 0),
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Carrier Metrics</h1>
            <p className="text-sm text-gray-500 mt-1">
              Per-carrier PNR volume, risk distribution, and dispatch activity
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => router.reload()} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100">
              Refresh
            </button>
            <Link href="/admin/cloudtraq_gds" className="text-sm text-blue-600 hover:underline">← Command Center</Link>
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold tabular-nums text-gray-700">{totals.pnr_count}</p>
            <p className="text-xs text-gray-500">Total PNRs</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold tabular-nums" style={{ color: totals.high_risk_count > 0 ? '#F44336' : '#4CAF50' }}>
              {totals.high_risk_count}
            </p>
            <p className="text-xs text-gray-500">High Risk PNRs</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold tabular-nums" style={{ color: totals.dispatch_count > 0 ? '#9C27B0' : '#9E9E9E' }}>
              {totals.dispatch_count}
            </p>
            <p className="text-xs text-gray-500">Total Dispatches</p>
          </div>
        </div>

        {/* Metrics table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Carrier</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">PNR Volume</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">High Risk</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Dispatches</th>
                <th className="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Risk Rate</th>
                <th className="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedMetrics.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-400 text-sm">
                    No carrier data available yet.
                  </td>
                </tr>
              ) : (
                sortedMetrics.map(m => {
                  const color     = CARRIER_COLORS[m.carrier_code] || '#607D8B'
                  const name      = CARRIER_NAMES[m.carrier_code] || m.carrier_code
                  const riskRate  = m.pnr_count > 0 ? Math.round((m.high_risk_count / m.pnr_count) * 100) : 0
                  const riskColor = riskRate >= 20 ? '#F44336' : riskRate >= 10 ? '#FF9800' : '#4CAF50'

                  return (
                    <tr key={m.carrier_code} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                            style={{ background: color }}
                          />
                          <div>
                            <p className="font-mono font-bold text-sm" style={{ color }}>{m.carrier_code}</p>
                            <p className="text-xs text-gray-400">{name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <MiniBar value={m.pnr_count} max={maxPnr} color={color} />
                      </td>
                      <td className="py-3 px-4">
                        <MiniBar value={m.high_risk_count} max={maxRisk} color={m.high_risk_count > 0 ? '#F44336' : '#4CAF50'} />
                      </td>
                      <td className="py-3 px-4">
                        <MiniBar value={m.dispatch_count || 0} max={maxDispatch} color="#9C27B0" />
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-sm font-bold tabular-nums" style={{ color: riskColor }}>
                          {riskRate}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <a
                          href={`/admin/cloudtraq_gds/anc_revenue?carrier_code=${m.carrier_code}`}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Revenue →
                        </a>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Nav */}
        <div className="mt-6 flex flex-wrap gap-4 text-sm text-gray-500">
          <Link href="/admin/cloudtraq_gds" className="text-blue-600 hover:underline">← Command Center</Link>
          <Link href="/admin/cloudtraq_gds/anc_revenue" className="text-blue-600 hover:underline">Anc Revenue</Link>
          <Link href="/admin/cloudtraq_gds/high_risk" className="text-blue-600 hover:underline">High Risk Candidates</Link>
        </div>
      </div>
    </div>
  )
}
